import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { cn, dateLocale } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, fmtD, pInitials, OPP_STAGES, OPP_TYPES, STAGE_TONE } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

export default function TeamOpportunities({ cid, onChanged }) {
  const [data, setData] = useState(null);
  const [stageF, setStageF] = useState("Toutes");
  const [dlg, setDlg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    api.get(`/clubs/${cid}/opportunities`).then((r) => setData(r.data)).catch(() => setData({ opportunities: [] }));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const opps = useMemo(() => data?.opportunities || [], [data]);
  const filtered = stageF === "Toutes" ? opps : opps.filter((o) => o.status === stageF);
  const metrics = useMemo(() => ({
    total: opps.length,
    active: opps.filter((o) => ["Prospection", "En discussion", "En attente"].includes(o.status)).length,
    value: opps.reduce((a, o) => a + (o.value || 0), 0),
  }), [opps]);

  const setStage = async (oid, status) => {
    try { await api.patch(`/clubs/${cid}/opportunities/${oid}`, { status }); load(); onChanged(); }
    catch { toast.error(tfr("Mise à jour impossible")); }
  };
  const del = async () => {
    try {
      await api.delete(`/clubs/${cid}/opportunities/${confirmDel.id}`);
      toast.success(tfr("Opportunité supprimée")); setConfirmDel(null); load(); onChanged();
    } catch { toast.error(tfr("Suppression impossible")); }
  };

  if (!data) return <div className="h-48 rounded-2xl bg-muted/60 animate-pulse" />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3" data-testid="opp-metrics">
        {[["Total", metrics.total], ["Actives", metrics.active], [tfr("Valeur enregistrée"), metrics.value ? `${metrics.value.toLocaleString(dateLocale())} €` : "—"]].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-3">
            <p className="text-[11px] text-muted-foreground">{k}</p>
            <p className="text-lg font-semibold tabular-nums">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {["Toutes", ...OPP_STAGES].map((s) => (
          <button key={s} onClick={() => setStageF(s)} data-testid={`opp-filter-${s}`}
            className={cn("h-9 px-3 rounded-full border text-xs transition-colors",
              stageF === s ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
            {tfr(s)}
          </button>
        ))}
        <Button size="sm" onClick={() => setDlg({})} className="h-9 gap-1 bg-athlynx text-white hover:bg-athlynx/90 ml-auto" data-testid="opp-add">
          <Plus className="h-3.5 w-3.5" /> {tfr("Créer une opportunité")}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Empty text={opps.length ? tfr("Aucune opportunité pour ce filtre.") : tfr("Aucune opportunité pour cette équipe.")}
          cta={opps.length ? null : tfr("Créer une opportunité")} onClick={() => setDlg({})} testid="opp-empty" />
      ) : (
        <div className="space-y-2" data-testid="opp-list">
          {filtered.map((o, i) => (
            <div key={o.id} className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-3.5" data-testid={`opp-card-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold flex-1 min-w-0 truncate">{o.title}</p>
                <Select value={o.status} onValueChange={(v) => setStage(o.id, v)}>
                  <SelectTrigger className={cn("h-7 w-[130px] text-[11px] rounded-full border", STAGE_TONE[o.status])} data-testid={`opp-stage-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{OPP_STAGES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => setDlg({ opp: o })} aria-label={tfr("Modifier l'opportunité")} data-testid={`opp-edit-${i}`}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => setConfirmDel(o)} aria-label={tfr("Supprimer l'opportunité")}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-muted-foreground">
                <span>{o.type}</span>
                {o.value != null && <span className="tabular-nums">{o.value.toLocaleString(dateLocale())} €</span>}
                {o.deadline && <span>Échéance : {fmtD(o.deadline)}</span>}
                {o.season && <span>{o.season}</span>}
              </div>
              {(o.athletes || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {o.athletes.map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-secondary/60 border border-border px-2 py-0.5 text-[10px]">
                      <span className="h-4 w-4 rounded-full bg-secondary flex items-center justify-center text-[8px] font-semibold">{pInitials(a.name)}</span>
                      {a.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dlg && <OppDialog cid={cid} opp={dlg.opp} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); onChanged(); }} />}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">{tfr("Supprimer cette opportunité ?")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{tfr("Cette action est définitive.")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>{tfr("Annuler")}</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={del} data-testid="opp-del-confirm">{tfr("Supprimer")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OppDialog({ cid, opp, onClose, onSaved }) {
  const [form, setForm] = useState(opp ? { ...opp } : { title: "", type: "Recrutement", status: "Prospection", value: "", deadline: "", season: "", criteria: "", description: "", athlete_ids: [], contact_id: "" });
  const [players, setPlayers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data || []));
    api.get(`/clubs/${cid}/contacts`).then((r) => setContacts(r.data || []));
  }, [cid]);

  const toggleAthlete = (pid) => {
    const cur = form.athlete_ids || [];
    set("athlete_ids", cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]);
  };

  const submit = async () => {
    if (!form.title.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        title: form.title, type: form.type, status: form.status,
        value: form.value === "" || form.value == null ? null : Number(form.value),
        deadline: form.deadline || "", season: form.season || "", criteria: form.criteria || "",
        description: form.description || "", athlete_ids: form.athlete_ids || [], contact_id: form.contact_id || null,
      };
      if (opp?.id) await api.patch(`/clubs/${cid}/opportunities/${opp.id}`, payload);
      else await api.post(`/clubs/${cid}/opportunities`, payload);
      toast.success(opp ? tfr("Opportunité modifiée") : tfr("Opportunité créée"));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Enregistrement impossible")); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="opp-dialog">
        <DialogHeader><DialogTitle className="text-base">{opp ? tfr("Modifier l'opportunité") : tfr("Créer une opportunité")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">{tfr("Titre / besoin")}</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={tfr("Ex. Recherche ailier U19")} className="h-10 mt-1" data-testid="opp-title" /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="opp-type"><SelectValue /></SelectTrigger>
              <SelectContent>{OPP_TYPES.map((t) => <SelectItem key={t} value={t}>{tfr(t)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{tfr("Statut")}</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="opp-status"><SelectValue /></SelectTrigger>
              <SelectContent>{OPP_STAGES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">{tfr("Valeur (€)")}</Label>
            <Input type="number" value={form.value ?? ""} onChange={(e) => set("value", e.target.value)} className="h-10 mt-1" data-testid="opp-value" /></div>
          <div><Label className="text-xs">{tfr("Échéance")}</Label>
            <Input type="date" value={form.deadline || ""} onChange={(e) => set("deadline", e.target.value)} className="h-10 mt-1" /></div>
          <div><Label className="text-xs">{tfr("Saison")}</Label>
            <Input value={form.season || ""} onChange={(e) => set("season", e.target.value)} placeholder="2026-2027" className="h-10 mt-1" /></div>
          <div><Label className="text-xs">{tfr("Contact responsable")}</Label>
            <Select value={form.contact_id || "none"} onValueChange={(v) => set("contact_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-10 mt-1 text-xs"><SelectValue placeholder={tfr("Sélectionner")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{tfr("Aucun")}</SelectItem>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="col-span-2"><Label className="text-xs">{tfr("Athlètes concernés")}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-[100px] overflow-y-auto">
              {players.map((p) => {
                const on = (form.athlete_ids || []).includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggleAthlete(p.id)} data-testid={`opp-athlete-${p.id}`}
                    className={cn("text-[11px] rounded-full border px-2.5 py-1 transition-colors",
                      on ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
                    {p.name}
                  </button>
                );
              })}
            </div></div>
          <div className="col-span-2"><Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1 resize-none" /></div>
        </div>
        <Button onClick={submit} disabled={!form.title.trim() || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="opp-submit">
          {opp ? tfr("Enregistrer") : tfr("Créer l'opportunité")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
